import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { usePortalLocale } from '@/lib/i18n/PortalLocaleContext';
import {
  PORTAL_NUMBER_LOCALE,
  type PortalLocale,
  type PortalTranslate,
} from '@/lib/i18n/portalLocale';
import { useMe } from '@/auth/useAuth';
import { managerFamilyId } from '@/auth/familyManager';
import { useAccountPath } from '@/app/accountSurface';
import { CourseMarkdown } from '@/components/CourseMarkdown';
import { MembershipOutcome } from './MembershipOutcome';
import {
  money,
  type Catalog,
  type Cycle,
  type Tier,
  type MembershipClient,
  type Content,
  type Summary,
} from './types';
function liveMembershipClient(family: string): MembershipClient {
  return {
    catalog: () => api<Catalog>('/membership/plans'),
    current: () => api<Summary>(`/families/${encodeURIComponent(family)}/membership`),
    action: (kind, body) =>
      api(`/families/${encodeURIComponent(family)}/membership/${kind}`, { method: 'POST', body }),
    resource: (id) =>
      api<Content>(
        `/families/${encodeURIComponent(family)}/membership/resources/${encodeURIComponent(id)}`,
      ),
  };
}
export function MembershipPage() {
  const { t } = usePortalLocale();
  const me = useMe(),
    family = managerFamilyId(me.data),
    path = useAccountPath();
  const client = useMemo(() => liveMembershipClient(family ?? ''), [family]);
  if (me.isLoading) return <p role="status">{t('Loading your account…')}</p>;
  if (!family)
    return (
      <section className="card-base">
        <h1 className="section-heading">{t('Membership')}</h1>
        <p className="lead-text mt-3">{t('Set up your account to manage a membership.')}</p>
        <Link className="btn-pill-secondary mt-4" to={path('/portal')}>
          {t('Back to your account')}
        </Link>
      </section>
    );
  return <MembershipWorkspace familyId={family} client={client} />;
}
export function MembershipPublicPage() {
  const { t } = usePortalLocale();
  return (
    <main className="mx-auto max-w-6xl space-y-6 px-4 py-8">
      <header>
        <img src="/logo-black-horizontal.png" alt="Airbotix" className="mb-6 h-10" />
        <h1 className="section-heading">
          {t('Learning and creation, with a clear family budget')}
        </h1>
        <p className="lead-text mt-3">
          {t('See a complete outcome before choosing your Airbotix membership.')}
        </p>
        <Link className="btn-pill-primary mt-4 inline-flex" to="/demo/membership">
          {t('Explore a prepared member workspace')}
        </Link>
      </header>
      <MembershipOutcome />
      <PublicPlans />
    </main>
  );
}
function PublicPlans() {
  const client = useMemo(() => liveMembershipClient(''), []);
  return <MembershipWorkspace familyId={null} client={client} showOutcome={false} />;
}
const statusLabel: Record<string, string> = {
  none: 'No membership',
  pending_payment: 'Payment is being checked',
  active: 'Active',
  cancel_at_period_end: 'Renewal cancelled',
  past_due: 'Renewal payment needs attention',
  expired: 'Membership period ended',
  reconciliation_required: 'Billing result needs checking',
};
const formatMembershipDate = (at: string | null, locale: PortalLocale, t: PortalTranslate) =>
  at
    ? new Date(at).toLocaleDateString(PORTAL_NUMBER_LOCALE[locale], {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : t('Not scheduled');
function safePaymentUrl(raw: string) {
  const u = new URL(raw);
  if (u.protocol !== 'https:' || u.username || u.password || !u.hostname.endsWith('.airwallex.com'))
    throw new Error('The billing link could not be verified.');
  return u.toString();
}
export function MembershipWorkspace({
  familyId,
  client,
  demo = false,
  showOutcome = true,
}: {
  familyId: string | null;
  client: MembershipClient;
  demo?: boolean;
  showOutcome?: boolean;
}) {
  const { t, locale } = usePortalLocale();
  const date = (at: string | null) => formatMembershipDate(at, locale, t);
  const num = (value: number) => value.toLocaleString(PORTAL_NUMBER_LOCALE[locale]);
  const { hash } = useLocation();
  const plansRef = useRef<HTMLHeadingElement>(null);
  const confirmRef = useRef<HTMLElement>(null),
    accountPath = useAccountPath();
  const qc = useQueryClient(),
    [cycle, setCycle] = useState<Cycle>('monthly'),
    [notice, setNotice] = useState(''),
    [resource, setResource] = useState<Content | null>(null),
    [pending, setPending] = useState<'checkout' | 'cancel' | 'resume' | 'change' | null>(null),
    [selected, setSelected] = useState<Tier>('plus');
  useEffect(() => {
    if (pending) confirmRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [pending, selected]);
  const form = useForm<{ accept_recurring: boolean }>({
    defaultValues: { accept_recurring: false },
  });
  const key = ['membership', demo ? 'demo' : 'live', familyId];
  const catalog = useQuery({
    queryKey: [...key, 'catalog'],
    queryFn: () => client.catalog(),
    retry: false,
  });
  const current = useQuery({
    queryKey: [...key, 'current'],
    queryFn: () => client.current(),
    enabled: !!familyId,
    refetchInterval: 60_000,
    retry: false,
  });
  // A marketing deep link arrives before these asynchronous sections settle.
  // Honour only its explicit plans fragment; ordinary entry still leads with the result.
  useEffect(() => {
    if (hash !== '#member-plans' || catalog.isPending || (familyId && current.isPending)) return;
    plansRef.current?.scrollIntoView?.({ block: 'start' });
    plansRef.current?.focus({ preventScroll: true });
  }, [hash, catalog.isPending, current.isPending, familyId]);
  const [requestIds] = useState(() => new Map<string, string>());
  const mutation = useMutation({
    mutationFn: async (kind: 'checkout' | 'cancel' | 'resume' | 'change' | 'reconcile') => {
      if (!catalog.data) throw new Error('Reload the plans before continuing.');
      const operationKey = `${kind}:${selected}:${cycle}:${current.data?.period_end ?? 'none'}:${catalog.data.revision}`;
      let request_id = requestIds.get(operationKey);
      if (!request_id) {
        request_id = crypto.randomUUID();
        requestIds.set(operationKey, request_id);
      }
      return client.action(kind, {
        request_id,
        ...(kind === 'checkout'
          ? {
              tier: selected,
              cycle,
              version: catalog.data.version,
              catalog_revision: catalog.data.revision,
              quote_token: catalog.data.quote_token,
              accept_recurring: true,
            }
          : kind === 'change'
            ? {
                tier: selected,
                cycle,
                version: catalog.data.version,
                catalog_revision: catalog.data.revision,
                quote_token: catalog.data.quote_token,
                accept_recurring: true,
              }
            : kind === 'resume'
              ? { accept_recurring: true }
              : {}),
      });
    },
    onSuccess: async (result) => {
      if (result.checkout_url) window.location.assign(safePaymentUrl(result.checkout_url));
      else {
        setNotice(
          demo
            ? t('Demo update applied to this isolated session.')
            : t('Billing request recorded. Check the confirmed state below.'),
        );
        requestIds.clear();
        setPending(null);
        await qc.invalidateQueries({ queryKey: key });
      }
    },
    onError: (e) =>
      setNotice(
        e instanceof Error
          ? t(e.message)
          : t('Billing could not be checked. Keep this operation and try checking its status.'),
      ),
  });
  const readResource = async (id: string) => {
    try {
      setResource(await client.resource(id));
    } catch (e) {
      setNotice(e instanceof Error ? t(e.message) : t('This content could not be opened.'));
    }
  };
  const state = current.data,
    active = !!state?.effective_tier,
    blocked = state?.status === 'reconciliation_required' || state?.status === 'pending_payment',
    chosen = catalog.data?.plans.find((p) => p.id === selected);
  return (
    <div className="space-y-6" data-membership-workspace>
      <header>
        <p className="eyebrow eyebrow-sky">{t('Airbotix membership')}</p>
        <h1 className="section-heading mt-2">{t('Membership & Stars')}</h1>
        <p className="mt-3 text-sm text-ink-soft">
          {t('One family membership. Published learning content and a monthly Stars allowance.')}
        </p>
      </header>
      {showOutcome ? <MembershipOutcome /> : null}
      {familyId ? (
        <section className="card-base" aria-label={t('Current membership')}>
          <h2 className="text-xl font-bold">{t('Your membership')}</h2>
          {current.isPending ? (
            <p role="status">{t('Loading membership…')}</p>
          ) : current.isError ? (
            <div role="alert">
              <p>{t('We could not check your membership. Your saved work has not changed.')}</p>
              <button className="btn-pill-secondary mt-3" onClick={() => void current.refetch()}>
                {t('Try again')}
              </button>
            </div>
          ) : state ? (
            <>
              <p className="mt-3 font-semibold">
                {state.effective_tier
                  ? `${state.effective_tier[0].toUpperCase()}${state.effective_tier.slice(1)} · `
                  : ''}
                {t(statusLabel[state.status] ?? 'State needs checking')}
              </p>
              <p className="mt-2 text-sm text-ink-soft">
                {t('Paid access ends: {date}.', { date: date(state.period_end) })}{' '}
                {state.cancel_at_period_end
                  ? t('No automatic renewal is scheduled.')
                  : t('Your billing agreement determines the next renewal.')}
              </p>
              {state.next_payment_at ? (
                <p className="mt-2 text-sm">
                  {t('Next payment: {date}.', { date: date(state.next_payment_at) })}
                </p>
              ) : null}
              {state.next_stars_at ? (
                <p className="mt-2 text-sm">
                  {t('Next paid-period Stars allowance: {date}.', {
                    date: date(state.next_stars_at),
                  })}
                </p>
              ) : null}
              {state.pending_change ? (
                <p className="mt-2 text-sm">
                  {t('{tier} {cycle} starts from {date}, after verified payment.', {
                    tier: state.pending_change.tier,
                    cycle: state.pending_change.cycle,
                    date: date(state.pending_change.effective_at),
                  })}
                </p>
              ) : null}
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  className="btn-pill-secondary"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate('reconcile')}
                >
                  {t('Check payment status')}
                </button>
                {active ? (
                  <button
                    className="btn-pill-ghost"
                    disabled={mutation.isPending || blocked}
                    onClick={() => {
                      setSelected(state.effective_tier ?? 'plus');
                      setCycle('monthly');
                      setPending(state.cancel_at_period_end ? 'resume' : 'cancel');
                      form.reset();
                    }}
                  >
                    {state.cancel_at_period_end
                      ? t('Resume automatic renewal')
                      : t('Cancel automatic renewal')}
                  </button>
                ) : null}
              </div>
            </>
          ) : null}
        </section>
      ) : null}
      {state?.content.length ? (
        <section className="card-base">
          <h2 className="text-xl font-bold">{t('Included learning content')}</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {state.content.map((c) => (
              <li key={c.id} className="rounded-2xl border border-hairline p-4">
                <h3 className="font-bold">{c.title}</h3>
                <p className="my-2 text-xs text-slate2">
                  {c.minimum_tier} ·{' '}
                  {c.kind === 'self_paced'
                    ? t('Self-paced course')
                    : c.kind === 'resource'
                      ? t('Complete learning resource')
                      : t('Eligible course discount')}
                </p>
                {c.kind === 'resource' ? (
                  <button className="btn-pill-secondary" onClick={() => void readResource(c.id)}>
                    {t('Open complete resource')}
                  </button>
                ) : (
                  <Link
                    className="btn-pill-secondary inline-flex"
                    to={accountPath(
                      c.kind === 'self_paced' ? '/portal/self-paced' : '/portal/courses',
                    )}
                  >
                    {t('Open course catalogue')}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {resource ? (
        <section className="card-base" aria-label={t('Complete member resource')}>
          <button className="btn-pill-ghost mb-4" onClick={() => setResource(null)}>
            {t('Close resource')}
          </button>
          <CourseMarkdown text={resource.body_md ?? ''} />
        </section>
      ) : null}
      <section className="card-base" aria-labelledby="member-plans">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            className="btn-pill-ghost"
            disabled={catalog.isFetching || mutation.isPending}
            onClick={() => {
              setPending(null);
              form.reset();
              void catalog.refetch();
            }}
          >
            {t('Refresh plans')}
          </button>
          <h2 id="member-plans" ref={plansRef} tabIndex={-1} className="scroll-mt-28 text-xl font-bold">
            {t('Choose your membership')}
          </h2>
          <p className="text-sm text-slate2">
            {t('Monthly billing · annual plans are not offered.')}
          </p>
        </div>
        {catalog.isPending ? (
          <p className="mt-4" role="status">
            {t('Loading plans…')}
          </p>
        ) : catalog.isError ? (
          <div className="mt-4" role="alert">
            <p>{t('Plans could not be loaded.')}</p>
            <button className="btn-pill-secondary mt-3" onClick={() => void catalog.refetch()}>
              {t('Try again')}
            </button>
          </div>
        ) : catalog.data ? (
          <>
            {!catalog.data.purchase_available ? (
              <p className="mt-4 rounded-2xl bg-surface p-3 text-sm">
                {t(
                  'Membership preview · sales are not open. No charge or membership activation is available.',
                )}
              </p>
            ) : null}
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {catalog.data.plans.map((p) => (
                <article key={p.id} className="rounded-3xl border border-hairline p-5">
                  <h3 className="text-lg font-bold">{p.name}</h3>
                  <p className="my-3 text-3xl font-extrabold">
                    {money(p.monthly_amount_cents)}
                    <span className="text-sm font-medium text-slate2">{t('/month')}</span>
                  </p>
                  <p className="font-bold">
                    {t('{stars} Stars each month', { stars: num(p.monthly_stars) })}
                  </p>
                  <p className="mt-2 text-sm text-ink-soft">
                    {t(
                      'Monthly membership Stars reset each paid month; unused Stars do not roll over. Recharge Stars are kept separately.',
                    )}
                  </p>
                  <p className="mt-3 text-sm">
                    {p.id === 'starter'
                      ? t('Member learning resources.')
                      : t('Member resources and explicitly included self-paced courses.')}
                  </p>
                  <p className="mt-2 text-sm">
                    {t('{percent}% off listed eligible courses; offers do not automatically combine.', {
                      percent: p.monthly_discount_bps / 100,
                    })}
                  </p>
                  {familyId ? (
                    <button
                      className="btn-pill-primary mt-5 w-full disabled:opacity-50"
                      disabled={
                        !catalog.data.purchase_available ||
                        blocked ||
                        mutation.isPending ||
                        current.isError ||
                        current.isPending
                      }
                      onClick={() => {
                        setSelected(p.id);
                        setPending(active ? 'change' : 'checkout');
                        form.reset();
                      }}
                    >
                      {active ? t('Review next-cycle change') : t('Review subscription')}
                    </button>
                  ) : (
                    <Link className="btn-pill-secondary mt-5 inline-flex" to="/portal/login">
                      {t('Sign in to manage membership')}
                    </Link>
                  )}
                </article>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate2">
              {t(
                'AI still spends Stars at its displayed server price. No unlimited AI. Classes and separately purchased products are included only when explicitly listed.',
              )}{' '}
              {catalog.data.tax_included === null
                ? t('Tax display awaits merchant configuration.')
                : catalog.data.tax_included
                  ? t('Displayed prices include configured tax.')
                  : t('Tax is shown in the final checkout total.')}
            </p>
          </>
        ) : null}
      </section>
      {pending ? (
        <section
          ref={confirmRef}
          className="card-base border-brand-sky"
          aria-labelledby="membership-confirm"
        >
          <h2 id="membership-confirm" className="text-xl font-bold">
            {pending === 'cancel'
              ? t('Confirm cancellation of automatic renewal')
              : pending === 'resume'
                ? t('Confirm automatic renewal')
                : pending === 'change'
                  ? t('Review next-cycle change')
                  : t('Review subscription')}
          </h2>
          <p className="mt-3 text-sm text-ink-soft">
            {pending === 'cancel'
              ? t(
                  'Paid access continues until {date}. Unused membership Stars expire at that boundary. Recharge Stars, saved projects and independently bought courses remain.',
                  { date: date(state?.period_end ?? null) },
                )
              : pending === 'change'
                ? t(
                    'Your current paid access remains until {date}. The new {tier} {cycle} plan ({price} per month, {stars} Stars each month) applies from the next paid cycle without an immediate proportional charge.',
                    {
                      date: date(state?.period_end ?? null),
                      tier: selected,
                      cycle,
                      price: money(chosen?.monthly_amount_cents ?? 0),
                      stars: chosen ? num(chosen.monthly_stars) : '',
                    },
                  )
                : t('{name} · {price} per month. Automatic renewal continues until cancelled.', {
                    name: chosen?.name ?? state?.tier ?? '',
                    price: money(chosen?.monthly_amount_cents ?? 0),
                  })}
          </p>
          <form
            className="mt-4 space-y-4"
            onSubmit={form.handleSubmit(() => mutation.mutate(pending))}
          >
            {pending !== 'cancel' ? (
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5"
                  {...form.register('accept_recurring', { required: true })}
                />
                <span>
                  {t(
                    'I have reviewed the amount, billing cycle, included content and automatic renewal.',
                  )}{' '}
                  {catalog.data?.terms_url ? (
                    <a
                      className="underline"
                      href={catalog.data.terms_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('Read membership terms')}
                    </a>
                  ) : null}
                </span>
              </label>
            ) : null}
            {form.formState.errors.accept_recurring ? (
              <p role="alert">{t('Review and accept automatic renewal before continuing.')}</p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                className="btn-pill-primary"
                disabled={
                  mutation.isPending || (pending !== 'cancel' && !form.watch('accept_recurring'))
                }
              >
                {mutation.isPending
                  ? t('Checking…')
                  : demo
                    ? t('Apply Demo change')
                    : pending === 'checkout'
                      ? t('Continue to secure checkout')
                      : t('Confirm request')}
              </button>
              <button
                type="button"
                className="btn-pill-secondary"
                disabled={mutation.isPending}
                onClick={() => setPending(null)}
              >
                {t('Keep current state')}
              </button>
            </div>
          </form>
        </section>
      ) : null}
      {notice ? (
        <p className="rounded-2xl border border-hairline bg-surface p-4 text-sm" role="status">
          {notice}
        </p>
      ) : null}
      {state ? (
        <section className="card-base">
          <h2 className="text-xl font-bold">{t('Stars and paid periods')}</h2>
          {typeof state.wallet_balance === 'number' ? (
            <p className="mt-3 font-semibold">
              {t('{stars} Stars available in your shared family wallet', {
                stars: num(state.wallet_balance),
              })}
            </p>
          ) : null}
          {typeof state.membership_stars === 'number' && typeof state.recharge_stars === 'number' ? (
            <dl className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-sm text-slate2">{t('Membership Stars · used first')}</dt>
                <dd className="font-bold">
                  {num(state.membership_stars)} / {num(state.membership_allowance ?? 0)}
                </dd>
                <dd className="text-sm">
                  {t('Expires: {date} · no rollover', {
                    date: date(state.membership_expires_at ?? null),
                  })}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-slate2">{t('Recharge & other permanent Stars')}</dt>
                <dd className="font-bold">{num(state.recharge_stars)}</dd>
                <dd className="text-sm">{t('Kept when membership resets or ends')}</dd>
              </div>
            </dl>
          ) : <p className="text-sm">{t('Balance sources have not been recorded.')}</p>}
          {demo && client.previewNextPaidMonth ? (
            <button
              className="btn-pill-secondary mt-4"
              onClick={async () => {
                await client.previewNextPaidMonth!();
                await current.refetch();
                setNotice(
                  t('Demo month advanced. Unused membership Stars expired; recharge Stars were kept.'),
                );
              }}
            >
              {t('Preview next month (Demo)')}
            </button>
          ) : null}
          {state.allowance_events?.map((e, i) => (
            <p className="mt-2 text-sm" key={i}>
              {date(e.created_at)} · {e.reason}: {t('{stars} Stars', { stars: num(e.delta_stars) })}
            </p>
          ))}
          <p className="mt-2 text-sm text-ink-soft">
            {t(
              'Membership Stars reset for each verified paid month. Unused membership Stars expire at the period end and do not roll over. Recharge and other permanent Stars remain. Spending uses membership Stars first.',
            )}
          </p>
          <div className="mt-4 grid gap-6 md:grid-cols-2">
            <div>
              <h3 className="font-bold">{t('Monthly allowance resets')}</h3>
              {state.grants.length ? (
                <ul className="mt-2 space-y-2 text-sm">
                  {state.grants.map((g) => (
                    <li key={g.id}>
                      {t('{date} · reset to {stars} member Stars (no rollover)', {
                        date: date(g.cycle_start),
                        stars: num(g.stars),
                      })}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm">{t('No membership allowance resets recorded.')}</p>
              )}
            </div>
            <div>
              <h3 className="font-bold">{t('Invoices')}</h3>
              {state.invoices.length ? (
                <ul className="mt-2 space-y-3 text-sm">
                  {state.invoices.map((i) => (
                    <li key={i.invoice_id}>
                      {money(i.amount_cents)} · {date(i.starts_at)}–{date(i.ends_at)}
                      {i.invoice_url ? (
                        <a
                          className="ml-2 underline"
                          href={safePaymentUrl(i.invoice_url)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t('Open invoice')}
                        </a>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm">{t('No verified membership invoices.')}</p>
              )}
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
